import * as React from "react"
import { cn } from "@/lib/utils"

type CommandContextValue = {
  search: string
  setSearch: (value: string) => void
}

type CommandListContextValue = {
  reportMatch: (id: string, matches: boolean | null) => void
  isEmpty: boolean
}

const CommandContext = React.createContext<CommandContextValue | undefined>(undefined)
const CommandListContext = React.createContext<CommandListContextValue | undefined>(undefined)

const useCommandContext = () => {
  const context = React.useContext(CommandContext)
  if (!context) {
    throw new Error("Command components must be used within <Command>")
  }
  return context
}

const useCommandListContext = () => React.useContext(CommandListContext)

const extractText = (children: React.ReactNode): string => {
  if (typeof children === "string") return children
  if (Array.isArray(children)) {
    return children.map(extractText).join(" ")
  }
  if (React.isValidElement(children)) {
    return extractText(children.props.children)
  }
  return ""
}

const Command = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const [search, setSearch] = React.useState("")
    const value = React.useMemo<CommandContextValue>(() => ({ search, setSearch }), [search])

    return (
      <CommandContext.Provider value={value}>
        <div
          ref={ref}
          className={cn(
            "flex h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-background text-foreground",
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </CommandContext.Provider>
    )
  },
)
Command.displayName = "Command"

const CommandInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, onChange, value, ...props }, ref) => {
    const { search, setSearch } = useCommandContext()

    return (
      <div className="flex items-center border-b border-border px-3" data-command-input-wrapper="">
        <input
          ref={ref}
          value={value ?? search}
          onChange={(event) => {
            setSearch(event.target.value)
            onChange?.(event)
          }}
          className={cn(
            "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        />
      </div>
    )
  },
)
CommandInput.displayName = "CommandInput"

const CommandList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const [matchMap, setMatchMap] = React.useState<Map<string, boolean>>(new Map())

    const reportMatch = React.useCallback<CommandListContextValue["reportMatch"]>((id, matches) => {
      setMatchMap((previous) => {
        const next = new Map(previous)
        if (matches === null) {
          next.delete(id)
        } else {
          next.set(id, matches)
        }
        return next
      })
    }, [])

    const isEmpty = React.useMemo(() => {
      if (matchMap.size === 0) return false
      for (const match of matchMap.values()) {
        if (match) return false
      }
      return true
    }, [matchMap])

    const contextValue = React.useMemo<CommandListContextValue>(
      () => ({
        reportMatch,
        isEmpty,
      }),
      [reportMatch, isEmpty],
    )

    return (
      <CommandListContext.Provider value={contextValue}>
        <div ref={ref} className={cn("max-h-60 overflow-y-auto overflow-x-hidden", className)} {...props}>
          {children}
        </div>
      </CommandListContext.Provider>
    )
  },
)
CommandList.displayName = "CommandList"

const CommandEmpty = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
  const context = useCommandListContext()
  if (!context || !context.isEmpty) {
    return null
  }
  return <div ref={ref} className={cn("py-6 text-center text-sm text-muted-foreground", className)} {...props} />
})
CommandEmpty.displayName = "CommandEmpty"

interface CommandGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  heading?: React.ReactNode
}

const CommandGroup = React.forwardRef<HTMLDivElement, CommandGroupProps>(
  ({ className, children, heading, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "overflow-hidden p-1 text-foreground/80 [&_[data-command-heading]]:px-2 [&_[data-command-heading]]:py-1.5 [&_[data-command-heading]]:text-xs [&_[data-command-heading]]:font-medium [&_[data-command-heading]]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {heading ? <div data-command-heading="">{heading}</div> : null}
      {children}
    </div>
  ),
)
CommandGroup.displayName = "CommandGroup"

type CommandItemProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  value?: string
  keywords?: string[]
  onSelect?: (value: string) => void
}

const CommandItem = React.forwardRef<HTMLButtonElement, CommandItemProps>(
  ({ className, children, value, keywords, onSelect, ...props }, ref) => {
    const { search } = useCommandContext()
    const listContext = useCommandListContext()
    const id = React.useId()

    const label = value ?? extractText(children)
    const normalizedLabel = label.toLowerCase()
    const normalizedSearch = search.trim().toLowerCase()
    const matches =
      normalizedSearch.length === 0 ||
      normalizedLabel.includes(normalizedSearch) ||
      (keywords ?? []).some((keyword) => keyword.toLowerCase().includes(normalizedSearch))

    React.useEffect(() => {
      if (!listContext) return
      listContext.reportMatch(id, matches)
      return () => listContext.reportMatch(id, null)
    }, [listContext, id, matches])

    if (!matches) {
      return null
    }

    const emittedValue = value ?? label

    return (
      <button
        type="button"
        ref={ref}
        className={cn(
          "relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none transition hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground",
          className,
        )}
        onClick={() => onSelect?.(emittedValue)}
        {...props}
      >
        {children}
      </button>
    )
  },
)
CommandItem.displayName = "CommandItem"

const CommandSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("-mx-1 h-px bg-muted", className)} {...props} />
  ),
)
CommandSeparator.displayName = "CommandSeparator"

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)} {...props} />
}
CommandShortcut.displayName = "CommandShortcut"

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
}
